import { Component, HostListener, Input, Output, EventEmitter, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { cloneDeep } from 'lodash';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { select, Store } from '@ngrx/store';
import { take } from 'rxjs';

import * as fromStore from 'src/app/_store/_reducers';
import { AuthService } from 'src/app/_shared/services';
import { FiltersPrefValue } from 'src/app/_shared/interface/filters-pref.interface';
import { UserPreferences } from 'src/app/_shared/interface/user-preferences.interface';
import { StateFilter } from 'src/app/_shared/interface/state-filter.interface';
import { OnDestroyMixin, untilComponentDestroyed } from 'src/app/_shared/classes/component-destroy.class';
import { StringFilter } from 'src/app/_shared/interface/string-filter.interface';
import { StatusFilter } from 'src/app/_shared/interface/status-filter.interface';
import { MatIcon } from '@angular/material/icon';
import { NgTemplateOutlet } from '@angular/common';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatIconButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [
    MatIcon,
    NgTemplateOutlet,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatSelect,
    MatOption,
    MatIconButton,
    TranslateModule,
  ],
})
export class FilterComponent extends OnDestroyMixin implements OnDestroy, OnInit {
  @Input() from: string;
  @Input() searchFilter: string;
  @Input() locationFilter: string;
  @Input() stateOptions: Array<StateFilter>;
  @Input() stateFilter: Array<StateFilter>;
  @Input() statusOptions: Array<StatusFilter>;
  @Input() statusFilter: Array<StatusFilter>;
  @Input() alarmOptions: Array<StringFilter>;
  @Input() alarmFilter: Array<string>;
  @Input() commsOptions: Array<StringFilter>;
  @Input() commsFilter: Array<string>;
  @Input() search: boolean;
  @Input() state: boolean;
  @Input() status: boolean;
  @Input() alarm: boolean;
  @Input() comms: boolean;
  @Input() clearButton: boolean;
  @Input() disable: boolean;
  @Input() userPreferenceName: string;

  @Output() searchFilterChange = new EventEmitter<string>();
  @Output() stateFilterChange = new EventEmitter<Array<StateFilter>>();
  @Output() statusFilterChange = new EventEmitter<Array<StatusFilter>>();
  @Output() alarmFilterChange = new EventEmitter<Array<string>>();
  @Output() commsFilterChange = new EventEmitter<Array<string>>();
  @Output() clearFilters = new EventEmitter<boolean>();

  @ViewChild('comboFilter') comboFilter: MatExpansionPanel;

  smallScreen: boolean;
  offsetSmallScreen: number;

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.smallScreen = window.innerWidth < this.offsetSmallScreen;
  }

  constructor(private store: Store<fromStore.State>, private authService: AuthService) {
    super();
  }

  ngOnInit(): void {
    this.offsetSmallScreen =
      [this.search, this.state, this.status, this.alarm, this.comms].filter(Boolean).length * 222 + 400;
    this.onResize();
    this.clearFilters.pipe(untilComponentDestroyed(this)).subscribe(() => {
      if (this.userPreferenceName) {
        this.store
          .pipe(select(fromStore.getUserPreferences))
          .pipe(take(1))
          .subscribe((userPrefs) => {
            let userPrefsClone: UserPreferences = cloneDeep(userPrefs);
            userPrefsClone.filters[this.userPreferenceName] = {};
            if (userPrefsClone !== userPrefs) {
              this.authService.savePreferences({ ...userPrefsClone }, true);
            }
          });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.userPreferenceName) {
      this.store
        .pipe(select(fromStore.getUserPreferences))
        .pipe(take(1))
        .subscribe((userPrefs) => {
          let userPrefsClone: UserPreferences = cloneDeep(userPrefs);
          let filterPrefs: FiltersPrefValue = {};
          let kpiFilters = {};

          if (this.state) {
            filterPrefs.stateFilter = this.stateFilter;
          }

          if (this.status) {
            filterPrefs.statusFilter = this.statusFilter;
          }

          if (this.alarm) {
            kpiFilters = { kpiFilters: this.alarmFilter };
          }

          if (this.comms) {
            filterPrefs.commsFilter = this.commsFilter;
          }

          filterPrefs.searchFilter = this.searchFilter;

          if (!userPrefsClone.filters) {
            userPrefsClone.filters = {};
          }
          userPrefsClone.filters[this.userPreferenceName] = filterPrefs;

          if (userPrefsClone !== userPrefs) {
            this.authService.savePreferences({ ...userPrefsClone, ...kpiFilters }, true);
          }
        });
    }
    super.ngOnDestroy();
  }

  expand(event): void {
    if (window.pageYOffset <= this.comboFilter._body?.nativeElement?.offsetTop) {
      window.scroll({
        top: this.comboFilter._body.nativeElement.offsetTop,
        behavior: 'smooth',
      });
    }
  }

  compareStates(state1: StateFilter, state2: StateFilter): boolean {
    return state1.name === state2.name;
  }

  compareStatus(status1: StatusFilter, status2: StatusFilter): boolean {
    return status1.name === status2.name;
  }
}
